import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Loader2, Pencil, AlertTriangle } from "lucide-react";
import { paymentMethodLabel } from "@/lib/paymentMethod";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PAYMENT_METHOD_OPTIONS = [
    { value: "cash", label: "Cash" },
    { value: "check", label: "Check" },
    { value: "ach", label: "ACH" },
    { value: "credit_card", label: "Credit card (Stripe)" },
    { value: "zelle", label: "Zelle" },
    { value: "other", label: "Other" },
];
const STATUS_LABELS = {
    not_collected: "Not collected",
    collected: "Collected",
    partially_applied: "Partially applied",
    fully_applied: "Fully applied",
    refunded: "Refunded",
    forfeited: "Forfeited",
};
const STATUS_CLASS = {
    not_collected: "bg-slate-100 text-slate-700",
    collected: "bg-emerald-100 text-emerald-800",
    partially_applied: "bg-amber-100 text-amber-800",
    fully_applied: "bg-blue-100 text-blue-800",
    refunded: "bg-blue-100 text-blue-800",
    forfeited: "bg-slate-200 text-slate-700",
};

const fmtMoney = (cents) => (Number(cents || 0) / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function DepositCard({ rental, currentUser, onChange }) {
    const dep = rental?.deposit || null;
    const status = dep?.status || "not_collected";
    const isSuper = currentUser?.role === "super_admin";
    const [recordOpen, setRecordOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);

    const refundLabel = (() => {
        if (!dep?.refund_action) return null;
        if (dep.refund_action === "stripe_refund") return `Stripe refund of ${fmtMoney(dep.refund_amount_cents)} in progress`;
        if (dep.refund_action === "credit_memo") return `Credit memo issued for ${fmtMoney(dep.refund_amount_cents)}`;
        if (dep.refund_action === "forfeit") return `Surplus forfeited`;
        return dep.refund_action;
    })();

    return (
        <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="deposit-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                    <ShieldCheck size={18} className="text-blue-900" />
                    <h2 className="text-base font-semibold text-slate-900">Security Deposit</h2>
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_CLASS[status] || STATUS_CLASS.not_collected}`}
                        data-testid="deposit-status">{STATUS_LABELS[status]}</span>
                </div>
                {status === "not_collected" && rental.status === "active" && (
                    <Button size="sm" onClick={() => setRecordOpen(true)}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="deposit-record-btn">Record deposit</Button>
                )}
                {status !== "not_collected" && isSuper && rental.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}
                        data-testid="deposit-edit-btn">
                        <Pencil size={13} className="mr-1.5" /> Edit
                    </Button>
                )}
            </div>

            {status === "not_collected" ? (
                <p className="mt-3 text-sm text-slate-500 italic">No deposit on file.</p>
            ) : (
                <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                    <Term label="Amount held" value={fmtMoney(dep.amount_cents)} testid="deposit-amount" />
                    <Term label="Payment method" value={paymentMethodLabel(dep.payment_method)} />
                    {dep.reference && <Term label="Reference" value={dep.reference} />}
                    <Term label="Collected on" value={fmtDate(dep.collected_at)} />
                    {(dep.applied_amount_cents || 0) > 0 && (
                        <Term label="Applied to invoices" value={fmtMoney(dep.applied_amount_cents)}
                            testid="deposit-applied" />
                    )}
                    {(dep.refund_amount_cents || 0) > 0 && (
                        <Term label="Refunded" value={fmtMoney(dep.refund_amount_cents)}
                            testid="deposit-refunded" />
                    )}
                    {refundLabel && (
                        <Term label="Refund mechanism" value={refundLabel}
                            testid="deposit-refund-label" />
                    )}
                    {dep.notes && <Term label="Notes" value={dep.notes} />}
                </dl>
            )}

            <DepositFormDialog
                open={recordOpen}
                onOpenChange={setRecordOpen}
                rental={rental}
                mode="record"
                onSuccess={onChange}
            />
            <DepositFormDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                rental={rental}
                deposit={dep}
                mode="edit"
                onSuccess={onChange}
            />
        </div>
    );
}

function Term({ label, value, testid }) {
    return (
        <div className={testid ? "" : ""} data-testid={testid}>
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className="text-slate-900">{value}</dd>
        </div>
    );
}

function DepositFormDialog({ open, onOpenChange, rental, deposit, mode, onSuccess }) {
    const isEdit = mode === "edit";
    const [amount, setAmount] = useState("");
    const [pm, setPm] = useState("");
    const [collectedOn, setCollectedOn] = useState("");  // Fix C — YYYY-MM-DD
    const [reference, setReference] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (isEdit && deposit) {
            setAmount(((deposit.amount_cents || 0) / 100).toFixed(2));
            setPm(deposit.payment_method || "");
            // Fix C — pre-fill the picker from the existing ISO timestamp.
            // Native ``<input type=date>`` expects ``YYYY-MM-DD``. We slice
            // the date portion off the canonical ``collected_at`` (which
            // is always UTC ISO 8601 server-side).
            setCollectedOn(deposit.collected_at ? String(deposit.collected_at).slice(0, 10) : "");
            setReference(deposit.reference || "");
            setNotes(deposit.notes || "");
        } else {
            // Task 8 Tweak C — Record (new) flow defaults the picker to
            // today's local date so the admin doesn't have to type it in.
            const today = new Date();
            const yyyy = today.getFullYear();
            const mm = String(today.getMonth() + 1).padStart(2, "0");
            const dd = String(today.getDate()).padStart(2, "0");
            setAmount(""); setPm(""); setCollectedOn(`${yyyy}-${mm}-${dd}`); setReference(""); setNotes("");
        }
    }, [open, isEdit, deposit]);

    const canSubmit = !!pm && parseFloat(amount || "0") > 0;

    const onSubmit = async () => {
        const cents = Math.round(parseFloat(amount) * 100);
        if (!Number.isFinite(cents) || cents <= 0) { toast.error("Amount must be positive"); return; }
        const body = {
            amount_cents: cents, payment_method: pm,
            reference: reference.trim(), notes: notes.trim(),
        };
        // Fix C — only forward when the picker carries a value. Empty
        // string would be rejected by the backend's strict YYYY-MM-DD
        // validator.
        if (collectedOn) body.collected_on = collectedOn;
        setBusy(true);
        try {
            if (isEdit) {
                await api.patch(`/admin/rentals/${rental.id}/deposit`, body);
                toast.success("Deposit updated");
            } else {
                await api.post(`/admin/rentals/${rental.id}/deposit/collect`, body);
                toast.success("Deposit recorded");
            }
            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={isEdit ? "deposit-edit-dialog" : "deposit-record-dialog"} className="max-w-md">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit security deposit" : "Record security deposit"}</DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? "Adjust an already-recorded deposit. Use carefully — corrections are audited."
                            : "Record receipt of a security deposit. The customer will be notified by email."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1">
                        <Label>Amount <span className="text-red-500">*</span></Label>
                        <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                            <Input type="number" min="0.01" step="0.01" value={amount}
                                onChange={e => setAmount(e.target.value)} className="pl-5"
                                data-testid="deposit-amount-input" />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <Label>Payment method <span className="text-red-500">*</span></Label>
                        <Select value={pm} onValueChange={setPm}>
                            <SelectTrigger data-testid="deposit-pm-trigger">
                                <SelectValue placeholder="Select method" />
                            </SelectTrigger>
                            <SelectContent>
                                {PAYMENT_METHOD_OPTIONS.map(o => (
                                    <SelectItem key={o.value} value={o.value}
                                        data-testid={`deposit-pm-${o.value}`}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {/* Task 8 Tweak C — Collected on date picker, between
                        Payment method and Reference. Shown in BOTH modes:
                        - Record (new): defaults to today, admin can adjust
                          for back-dated deposits.
                        - Edit:         pre-fills from ``deposit.collected_at``;
                          super-admins can correct the stored date.
                    */}
                    <div className="space-y-1">
                        <Label>Collected on {isEdit && <span className="text-red-500">*</span>}</Label>
                        <Input
                            type="date"
                            value={collectedOn}
                            onChange={(e) => setCollectedOn(e.target.value)}
                            data-testid="deposit-collected-on-input"
                        />
                        <p className="text-[11px] text-slate-500">
                            {isEdit
                                ? "Correct the date this deposit was actually collected. A separate history entry is recorded on every change."
                                : "Defaults to today — change if the funds were received on a different date."}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <Label>Reference (optional)</Label>
                        <Input value={reference}
                            onChange={e => setReference(e.target.value.slice(0, 200))}
                            placeholder="Check #, ACH txn id, Stripe pi_..."
                            data-testid="deposit-ref-input" />
                    </div>
                    <div className="space-y-1">
                        <Label>Notes (optional)</Label>
                        <Textarea rows={2} value={notes}
                            onChange={e => setNotes(e.target.value.slice(0, 1100))}
                            data-testid="deposit-notes-input" />
                        <p className="text-[11px] text-slate-500">{notes.length}/1000</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button disabled={!canSubmit || busy} onClick={onSubmit}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid={isEdit ? "deposit-edit-submit" : "deposit-record-submit"}>
                        {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}
                        {isEdit ? "Save changes" : "Record deposit"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
